import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import {
  dateToString,
  dateToTimestamp,
  stringToDate,
  timestampToDate,
  unNullifyArrays,
  unstringifyBooleans,
} from 'src/utils/fieldNormalizers';

import { Cart, CartItem, PriceBreakdownItem } from './_types';

const readOnlyCartItemKeys: string[] = ['name'];
const booleanKeys: string[] = [];
const arrayKeys: string[] = ['cartItems', 'priceBreakdown'];
const dateKeys: string[] = [];
const dateStringKeys: string[] = [
  'startOn',
  'backdate',
  'cancelDate',
  'expiresOn',
  'activationDate',
  'start_on',
  'cancel_date',
  'expires_on',
  'activation_date',
];

export function normalizeCart(cart: any): Cart {
  let camelized = camelcaseKeys(cart, { deep: true });
  camelized = unstringifyBooleans(camelized, booleanKeys);
  camelized = unNullifyArrays(camelized, arrayKeys);

  dateKeys.forEach((key) => {
    if (camelized[key]) {
      camelized[key] = dateToTimestamp(camelized[key]);
    }
  });

  dateStringKeys.forEach((key) => {
    if (camelized[key]) {
      camelized[key] = stringToDate(camelized[key]);
    }
  });

  if (camelized.cartItems) {
    camelized.cartItems = camelized.cartItems.map(normalizeCartItem);
  }

  if (camelized.priceBreakdown) {
    camelized.priceBreakdown = camelized.priceBreakdown.map(
      normalizePriceBreakdownItem
    );
  }

  return camelized;
}

export function denormalizeCart(cart: Partial<Cart>): any {
  const decamelized = decamelizeKeys(cart, { deep: true });

  if (!decamelized.user_id && decamelized.guest_email) {
    decamelized.guest_checkout = true;
  }

  if (decamelized.cart_items) {
    decamelized.cart_items = decamelized.cart_items.map(denormalizeCartItem);
  }

  dateStringKeys.forEach((key) => {
    if (decamelized[key]) {
      decamelized[key] = dateToString(decamelized[key]);
    }
  });

  if (decamelized.payment_method) {
    if (typeof decamelized.payment_method === 'string') {
      decamelized.payment_method_type_or_id = decamelized.payment_method;
    } else if (decamelized.payment_method.type === 'other') {
      decamelized.payment_method_type_or_id =
        decamelized.payment_method.type.toString();
      decamelized.external_payment_method =
        decamelized.payment_method.id.toString();
    } else if (decamelized.payment_method.type === 'card_present') {
      decamelized.payment_method_type_or_id = 'card_swipe';
      //decamelized.card_reader_id = decamelized.payment_method.id;
    } else {
      decamelized.payment_method_type_or_id =
        decamelized.payment_method.id.toString();
    }

    delete decamelized.payment_method;
  }

  return decamelized;
}

export function normalizeCartItem(item: any): CartItem {
  const camelized = camelcaseKeys(item, { deep: true });

  dateKeys.forEach((key) => {
    if (camelized[key]) {
      camelized[key] = timestampToDate(camelized[key]);
    }
  });

  dateStringKeys.forEach((key) => {
    if (camelized[key]) {
      camelized[key] = stringToDate(camelized[key]);
    }
  });

  return camelized;
}

export function denormalizeCartItem(item: Partial<CartItem>): any {
  const decamelized = decamelizeKeys(item) as any;

  readOnlyCartItemKeys.forEach((key) => {
    if (decamelized[key]) {
      delete decamelized[key];
    }
  });

  dateStringKeys.forEach((key) => {
    if (decamelized[key]) {
      decamelized[key] = dateToString(decamelized[key]);
    }
  });

  dateKeys.forEach((key) => {
    if (decamelized[key]) {
      decamelized[key] = timestampToDate(decamelized[key]);
    }
  });

  if (decamelized.charged_by && typeof decamelized.charged_by === 'object') {
    decamelized.charged_by_id = decamelized.charged_by.id;
  }

  if (
    decamelized.override_proration_behavior &&
    typeof decamelized.override_proration_behavior === 'object'
  ) {
    decamelized.override_proration_behavior =
      decamelized.override_proration_behavior.value;
  }

  if (
    decamelized.billing_cycle_anchor &&
    typeof decamelized.billing_cycle_anchor === 'object'
  ) {
    decamelized.billing_cycle_anchor = decamelized.billing_cycle_anchor.value;
  }

  // Ensure billing_cycle_anchor is null/undefined if it's empty, blank, or "no_anchor"
  if (decamelized.billing_cycle_anchor) {
    const anchor = decamelized.billing_cycle_anchor;
    if (
      anchor === '' ||
      anchor === 'no_anchor' ||
      (typeof anchor === 'string' && anchor.trim() === '')
    ) {
      delete decamelized.billing_cycle_anchor;
    }
  }

  if (
    decamelized.primary_commissionees &&
    decamelized.primary_commissionees.every((u) => typeof u === 'object')
  ) {
    decamelized.primary_commissionee_ids =
      decamelized.primary_commissionees.map((user) => user.id);
  }

  if (
    decamelized.secondary_commissionees &&
    decamelized.secondary_commissionees.every((u) => typeof u === 'object')
  ) {
    decamelized.secondary_commissionee_ids =
      decamelized.secondary_commissionees.map((user) => user.id);
  }

  if (decamelized.item?.paymentOption) {
    decamelized.payment_option = decamelizeKeys(decamelized.item.paymentOption);
  }

  if (decamelized.users) {
    decamelized.user_ids = decamelized.users.map((user) => user.id);
  }

  if (decamelized.share_with_all_dependents) {
    decamelized.share_with_all_dependents = decamelized.user_id;
  }

  delete decamelized.item;
  delete decamelized.primary_commissionees;
  delete decamelized.secondary_commissionees;
  delete decamelized.charged_by;
  delete decamelized.users;

  return decamelized;
}

export function normalizePriceBreakdownItem(item: any): PriceBreakdownItem {
  const camelized = camelcaseKeys(item, { deep: true });

  if (camelized.anchorDate) {
    camelized.anchorDate = camelized.anchorDate
      ? new Date(camelized.anchorDate)
      : null;
  }

  return camelized;
}

export function denormalizePriceBreakdownItem(
  item: Partial<PriceBreakdownItem>
): any {
  const decamelized = decamelizeKeys(item, { deep: true });

  return decamelized;
}

export function normalizeSearchParams(
  params: Record<string, any>
): Record<string, any> {
  return {
    ...params,
    location_ids: params.location_ids
      ? Array.isArray(params.location_ids)
        ? params.location_ids.map((id) => String(id)).join(',')
        : params.location_ids
      : null,
    package_tags: params.package_tags
      ? Array.isArray(params.package_tags)
        ? params.package_tags.map((tag) => String(tag)).join(',')
        : params.package_tags
      : null,
    service_ids: params.service_ids
      ? Array.isArray(params.service_ids)
        ? params.service_ids.map((id) => String(id)).join(',')
        : params.service_ids
      : null,
    trainer_ids: params.trainer_ids
      ? Array.isArray(params.trainer_ids)
        ? params.trainer_ids.map((id) => String(id)).join(',')
        : params.trainer_ids
      : null,
    no_user: params.no_user || null,
  };
}
